import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/style.css";
import pass from "../assets/images/pass.png";
import door from "../assets/images/door1.png";
import lg from "../assets/images/lg.png";
import ac from "../assets/images/ac.png";
import api, { IMAGE_BASE_URL } from "../api/Api.js";
import { useLanguage } from "../context/LanguageContext";

function SendRequestCar() {
  const { language, t } = useLanguage();
  const navigate = useNavigate();

  const tt = (key, fallback = "") =>
    t(`requestcar.${key}`, fallback);

  const getLanguageId = () => {
    if (typeof language === "number" || !isNaN(Number(language))) {
      return Number(language);
    }

    if (language && typeof language === "object") {
      return (
        Number(
          language.lang_id || language.language_id || language.id
        ) || 1
      );
    }

    return 1;
  };

  const languageId = getLanguageId();

  // =====================================================
  // STATE
  // =====================================================

  const [requestCar, setRequestCar] = useState(null);
  const [criteria, setCriteria] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [form, setForm] = useState({
    selTitle: "Mr.",
    firstname: "",
    lastname: "",
    txtphone: "",
    txtmobile: "",
    txtaddress: "",
    txtpostcode: "",
    txtemail: "",
    txtconfirmemail: "",
    txtcity: "",
    txtcountry: "",
    txtpickdetails: "",
    txtdropdetails: "",
  });

  // =====================================================
  // LOAD DATA FROM SESSION STORAGE
  // (set by BookACar.jsx handleSendRequest())
  // =====================================================

  useEffect(() => {
    try {
      const storedCar = sessionStorage.getItem("requestCar");
      const storedCriteria = sessionStorage.getItem("bookingCriteria");

      if (!storedCar) {
        console.warn("No requestCar found in sessionStorage");
        navigate("/");
        return;
      }

      setRequestCar(JSON.parse(storedCar));

      if (storedCriteria) {
        setCriteria(JSON.parse(storedCriteria));
      }
    } catch (error) {
      console.error("Error reading request car data:", error);
      navigate("/");
    }
  }, [navigate]);

  // =====================================================
  // CAR IMAGE (same helper as BookACar.jsx)
  // =====================================================

  const getCarImage = (image) => {
    if (!image) return "";

    let imagePath = String(image).trim();

    if (!imagePath) return "";

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath;
    }

    imagePath = imagePath.replace(/^\/+/, "");

    const imageBase =
      IMAGE_BASE_URL || "http://localhost:5555/uploads";

    if (imagePath.startsWith("images/")) {
      return `${imageBase}/${imagePath}`;
    }

    if (imagePath.startsWith("CarImage/")) {
      return `${imageBase}/images/${imagePath}`;
    }

    return `${imageBase}/images/CarImage/${imagePath}`;
  };

  // =====================================================
  // FORM CHANGE HANDLER
  // =====================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================================
  // VALIDATION
  // Mirrors the jQuery validate rules in
  // requestpersonaldetails.php
  // =====================================================

  const validate = () => {
    const errors = {};

    if (!form.firstname.trim() || form.firstname.trim().length < 2) {
      errors.firstname = tt(
        "firstNameRequired",
        "First name is required (min 2 characters)"
      );
    } else if (!/^[a-zA-Z/. ]+$/.test(form.firstname.trim())) {
      errors.firstname = tt(
        "firstNameInvalid",
        "Remove special characters"
      );
    }

    if (!form.lastname.trim() || form.lastname.trim().length < 2) {
      errors.lastname = tt(
        "lastNameRequired",
        "Last name is required (min 2 characters)"
      );
    } else if (!/^[a-zA-Z ]+$/.test(form.lastname.trim())) {
      errors.lastname = tt(
        "lastNameInvalid",
        "Remove special characters"
      );
    }

    if (!/^\S+@\S+\.\S+$/.test(form.txtemail)) {
      errors.txtemail = tt(
        "emailInvalid",
        "Please enter a valid email address"
      );
    }

    if (form.txtemail !== form.txtconfirmemail) {
      errors.txtconfirmemail = tt(
        "emailsMustMatch",
        "Email and confirm email must match"
      );
    }

    if (!form.txtaddress.trim()) {
      errors.txtaddress = tt(
        "addressRequired",
        "Address is required"
      );
    } else if (form.txtaddress.length > 299) {
      errors.txtaddress = tt(
        "addressTooLong",
        "Address is too long"
      );
    }

    if (!form.txtcity.trim()) {
      errors.txtcity = tt("cityRequired", "City is required");
    }

    if (!form.txtcountry.trim()) {
      errors.txtcountry = tt(
        "countryRequired",
        "Country is required"
      );
    }

    if (!form.txtmobile.trim()) {
      errors.txtmobile = tt(
        "mobileRequired",
        "Mobile number is required"
      );
    } else if (!/^[0-9\-+ ]+$/.test(form.txtmobile.trim())) {
      errors.txtmobile = tt(
        "mobileInvalid",
        "Please enter a valid mobile number"
      );
    }

    if (form.txtphone && !/^[0-9\-+ ]+$/.test(form.txtphone.trim())) {
      errors.txtphone = tt(
        "phoneInvalid",
        "Please enter a valid phone number"
      );
    }

    if (
      form.txtpostcode &&
      !/^[a-zA-Z0-9 ]+$/.test(form.txtpostcode.trim())
    ) {
      errors.txtpostcode = tt(
        "postcodeInvalid",
        "Please enter a valid postcode"
      );
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  // =====================================================
  // SUBMIT
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitError("");

    if (!validate()) {
      return;
    }

    if (!requestCar) {
      setSubmitError(
        tt("carMissing", "Car information is missing.")
      );
      return;
    }

    const selectedmodelid =
      requestCar.selectedmodelid ||
      requestCar.carmodel_id ||
      requestCar.id;

    const selectedcatid =
      requestCar.selectedcatid ||
      requestCar.cat_id ||
      requestCar.category_id;

    const payload = {
      ...form,

      cmbPickCity: criteria?.cmbPickCity || 1,
      cmbPickLocation: criteria?.cmbPickLocation || "",
      pickdate: criteria?.pickdate || "",
      cmbpickHour: criteria?.cmbpickHour || "10",
      cmbpickmin: criteria?.cmbpickmin || "00",

      cmbreturnCity: criteria?.cmbreturnCity || 1,
      cmbDropLocation: criteria?.cmbDropLocation || "",
      returndate: criteria?.returndate || "",
      cmbreturnHour: criteria?.cmbreturnHour || "10",
      cmbreturnmin: criteria?.cmbreturnmin || "00",

      selectedmodelid,
      selectedcatid,
      car_price:
        requestCar.finalPrice || requestCar.pricePerDay || 0,
      diffdate: requestCar.rentalDays || criteria?.diffdate || 1,
      diffhour: 0,

      selectedlanguage: languageId,
      txtdrvage: criteria?.selecteddrvage || 0,

      clientid: "",
      clientemail: "",
    };

    try {
      setSubmitting(true);

      console.log("Submitting car request:", payload);

      const response = await api.post("/bookings/request", payload);

      console.log("Car request response:", response.data);

      if (response.data && response.data.success) {
        sessionStorage.setItem(
          "requestSentData",
          JSON.stringify({
            car: requestCar,
            form,
            criteria,
            fullbooking_no: response.data.fullbooking_no,
          })
        );

        sessionStorage.removeItem("requestCar");

        navigate("/requestsent");
        return;
      } else {
        setSubmitError(
          response.data?.message ||
            tt("submitFailed", "Unable to send your request.")
        );
      }
    } catch (error) {
      console.error("Car request submit error:", error);
      console.error("Error response:", error.response);

      setSubmitError(
        error.response?.data?.message ||
          tt(
            "submitFailed",
            "Unable to send your request. Please try again."
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (!requestCar) {
    return (
      <div
        className="container text-center"
        style={{ paddingTop: "100px", paddingBottom: "100px" }}
      >
        <p>{tt("loading", "Loading...")}</p>
      </div>
    );
  }

  const carImage = getCarImage(
    requestCar.carmodel_image || requestCar.image
  );

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="p20">
      <div className="p0 container container2  p0">

        {/* =====================================================
            CAR SUMMARY (mirrors requestcardetailsfinal.php)
        ===================================================== */}






        
          <div
            className=" col-md-3 sidebar" style={{float:"left"}}
          > 
		 
		  <div className="your-car">Your Car</div> 
		  <div className="car-box price-box">
		  <h2 className="car-title" style={{textAlign: "center", fontSize: "20px",  lineHeight: "19px"}}>
		   {requestCar.cat_name  ||
                  ""}</h2>
		  <p style={{fontSize: "14px", textAlign: "center",  fontWeight: "500"}}> {requestCar.cat_name  || ""}</p>
		  
            <div className="carimg">
              {carImage ? (
                <img
                  src={carImage}
                  alt={requestCar.carmodel_name || "Car"}
                  className="car-img"
                  width="193"
                  height="126"
                  
                />
              ) : (
                <div
                  style={{
                    width: "193px",
                    height: "126px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #1b9ab5",
                    borderRadius: "5px",
                    color: "#999",
                  }}
                >
                  {tt("noImage", "No Image")}
                </div>
              )}
            </div>




<div className="specs-row" style={{textAlign: "center",  margin: "0 14px"}}>
                    <span className="spec-tag">
					<img
                src={pass}
                alt="passengers"
               
              /> 
					  {requestCar.carmodel_passgrno ??
                    requestCar.passengers ??
                    0}</span>
                    <span className="spec-tag">		<img
                src={lg}
                alt="luggage"
               
              /> {requestCar.carmodel_luggno ??
                    requestCar.luggage ??
                    0}</span>
                    <span className="spec-tag">
					<img
                src={door}
                alt="doors"
               
              />
					 {requestCar.carmodel_doors ?? "-"}</span>
                    <span className="spec-tag"> 
					<img
                src={ac}
                alt="airconditionr"
               
              />
					   {requestCar.carmodel_airconditionr ||
                    requestCar.airConditioning ||
                    tt("no", "No")}</span>
                </div>
<p className="big-size-text1" style={{color: "red", fontSize: "13px"}}>{requestCar.availabilityMessage}</p>

            <div className="requestcarsec" style={{ flex: 1 }}>
              <div className="carname" style={{ fontWeight: "bold" }}>
               
              </div>

             
           

            

              {requestCar.availabilityMessage && (
                <div
                  style={{
                    marginTop: "15px",
                    color: "#d9302f",
                    fontSize: "13px",
                  }}
                >
                  
                </div>
              )}
            </div>
          </div>
       </div>

        {/* =====================================================
            PERSONAL DETAILS FORM
            (mirrors requestpersonaldetails.php)
        ===================================================== */}

        <div
          className="col-md-9 main-content"
         style={{float:"left"}}
        >
		
	
          <div className="rate" style={{ padding: "10px" }}>
            <strong>
              {tt("personalDetails", "Personal details")} :
            </strong>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="price-box" style={{ padding: "15px" }}>

              <div className="row">

                {/* TITLE */}
                <div className="col-md-4 form-group">
                  <label>{tt("title", "Title")} *</label>
                  <select
                    name="selTitle"
                    className="form-control"
                    value={form.selTitle}
                    onChange={handleChange}
                  >
                    <option value="Mr.">Mr.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Dr.">Dr.</option>
                  </select>
                </div>

               

                {/* FIRST NAME */}
                <div className="col-md-4 form-group">
                  <label>{tt("firstName", "First name")} *</label>
                  <input
                    type="text"
                    name="firstname"
                    className="form-control"
                    maxLength={30}
                    value={form.firstname}
                    onChange={handleChange}
                  />
                  {fieldErrors.firstname && (
                    <div className="text-danger">
                      {fieldErrors.firstname}
                    </div>
                  )}
                </div>
				
				{/* LAST NAME */}
                <div className="col-md-4 form-group">
                  <label>{tt("familyName", "Family name")} *</label>
                  <input
                    type="text"
                    name="lastname"
                    className="form-control"
                    maxLength={30}
                    value={form.lastname}
                    onChange={handleChange}
                  />
                  {fieldErrors.lastname && (
                    <div className="text-danger">
                      {fieldErrors.lastname}
                    </div>
                  )}
                </div>
				
			

                {/* MOBILE */}
                <div className="col-md-4 form-group">
                  <label>{tt("mobileNo", "Mobile no")} *</label>
                  <input
                    type="text"
                    name="txtmobile"
                    className="form-control"
                    maxLength={15}
                    value={form.txtmobile}
                    onChange={handleChange}
                  />
                  {fieldErrors.txtmobile && (
                    <div className="text-danger">
                      {fieldErrors.txtmobile}
                    </div>
                  )}
                </div>

                  {/* EMAIL */}
                <div className="col-md-4 form-group">
                  <label>{tt("emailId", "Email ID")} *</label>
                  <input
                    type="text"
                    name="txtemail"
                    className="form-control"
                    maxLength={70}
                    value={form.txtemail}
                    onChange={handleChange}
                  />
                  {fieldErrors.txtemail && (
                    <div className="text-danger">
                      {fieldErrors.txtemail}
                    </div>
                  )}
                </div>
				
				{/* CONFIRM EMAIL */}
                <div className="col-md-4 form-group">
                  <label>
                    {tt("confirmEmail", "Confirm email")} *
                  </label>
                  <input
                    type="text"
                    name="txtconfirmemail"
                    className="form-control"
                    maxLength={70}
                    value={form.txtconfirmemail}
                    onChange={handleChange}
                  />
                  {fieldErrors.txtconfirmemail && (
                    <div className="text-danger">
                      {fieldErrors.txtconfirmemail}
                    </div>
                  )}
                </div>
				
				 {/* HOME ADDRESS */}
                <div className="col-md-8 form-group">
                  <label>{tt("homeAddress", "Home Address")} *</label>
                  <textarea
                    name="txtaddress"
                    className="form-control"
                    rows={4}
                    value={form.txtaddress}
                    onChange={handleChange}
					style={{width: "100%", height: "160px !important"}}
                  />
                  {fieldErrors.txtaddress && (
                    <div className="text-danger">
                      {fieldErrors.txtaddress}
                    </div>
                  )}
                </div>


<div className="col-md-4 form-group">
{/* ZIP CODE */}
                <div className="col-md-12 form-group" style={{padding: "0px"}}>
                  <label>{tt("zipCode", "Zip code")}</label>
                  <input
                    type="text"
                    name="txtpostcode"
                    className="form-control"
                    maxLength={15}
                    value={form.txtpostcode}
                    onChange={handleChange}
                  />
                  {fieldErrors.txtpostcode && (
                    <div className="text-danger">
                      {fieldErrors.txtpostcode}
                    </div>
                  )}
                </div>

               

                {/* CITY */}
                <div className="col-md-12 form-group" style={{padding: "0px"}}>
                  <label>{tt("city", "City")} *</label>
                  <input
                    type="text"
                    name="txtcity"
                    className="form-control"
                    maxLength={30}
                    value={form.txtcity}
                    onChange={handleChange}
                  />
                  {fieldErrors.txtcity && (
                    <div className="text-danger">
                      {fieldErrors.txtcity}
                    </div>
                  )}
                </div>

              

                {/* COUNTRY */}
                <div className="col-md-12 form-group" style={{padding: "0px"}}>
                  <label>{tt("country", "Country")} *</label>
                  <input
                    type="text"
                    name="txtcountry"
                    className="form-control"
                    maxLength={30}
                    value={form.txtcountry}
                    onChange={handleChange}
                    placeholder={tt(
                      "countryPlaceholder",
                      "e.g. United Kingdom"
                    )}
                  />
                  {fieldErrors.txtcountry && (
                    <div className="text-danger">
                      {fieldErrors.txtcountry}
                    </div>
                  )}
                </div>



</div>




                
                

                {/* PICKUP COMMENTS */}
                <div className="col-md-6 form-group">
                  <label>
                    {tt("pickupComments", "Pick up comments")}
                  </label>
                  <textarea
                    name="txtpickdetails"
                    className="form-control"
                    rows={2}
                    value={form.txtpickdetails}
                    onChange={handleChange}
                  />
                </div>

                {/* DROP COMMENTS */}
                <div className="col-md-6 form-group">
                  <label>
                    {tt("dropoffComments", "Drop off comments")}
                  </label>
                  <textarea
                    name="txtdropdetails"
                    className="form-control"
                    rows={2}
                    value={form.txtdropdetails}
                    onChange={handleChange}
                  />
                </div>

              </div>

              {submitError && (
                <div className="text-danger" style={{ marginTop: "10px" }}>
                  {submitError}
                </div>
              )}

              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <button
                  type="submit"
                  className="nextbutton1 nextbutton"
                  disabled={submitting}
                  style={{ width: "200px" }}
                >
                  {submitting
                    ? tt("sending", "Sending...")
                    : tt("sendRequest", "Send Request")}
                </button>
              </div>

            </div>
          </form>
        </div>

      </div>
    </div>
  );
}

export default SendRequestCar;
